import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Typography } from 'antd';
import { describeSaasCoverage } from '../../utils/dattoHelpers';
import {
  CloudOutlined,
  CloudServerOutlined,
  GoogleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

const ProviderCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const ProviderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const ProviderIcon = styled.div`
  font-size: 28px;
  color: ${({ $color }) => $color};
`;

const ProviderName = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const MetricLabel = styled(Text)`
  font-size: 13px;
  color: var(--odin-text-secondary);
`;

const MetricValue = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--odin-border);
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $status, theme }) => {
    if ($status === 'protected') return theme.colors.success;
    if ($status === 'warning') return theme.colors.warning;
    return theme.colors.error;
  }};
`;

const StatusText = styled(Text)`
  font-size: 12px;
  color: ${({ $status, theme }) => {
    if ($status === 'protected') return theme.colors.success;
    if ($status === 'warning') return theme.colors.warning;
    return theme.colors.error;
  }};
`;

const LastBackupText = styled(Text)`
  font-size: 11px;
  color: var(--odin-text-tertiary);
  margin-top: 4px;
`;

/**
 * SaaS Provider Summary Card
 * Displays summary for a single SaaS provider (M365, Azure, etc.)
 *
 * @param {object} props
 * @param {string} props.provider - Provider name (e.g., 'M365', 'Azure')
 * @param {Array} props.assets - Array of assets for this provider
 * @param {string} props.icon - Ant Design icon name
 * @param {string} props.color - Brand color for this provider
 */
const SaasProviderCard = ({ provider, assets, icon, color }) => {
  // Get icon component from name string
  const iconMap = {
    CloudOutlined: CloudOutlined,
    CloudServerOutlined: CloudServerOutlined,
    GoogleOutlined: GoogleOutlined,
  };
  const IconComponent = iconMap[icon] || CloudOutlined;

  // Calculate metrics
  const protectedCount = assets.filter(a => a.status === 'protected').length;
  const unprotectedCount = assets.filter(a => a.status === 'unprotected').length;
  const warningCount = assets.filter(a => a.status === 'warning').length;

  // Determine overall status
  let overallStatus = 'protected';
  if (unprotectedCount > 0) {
    overallStatus = 'unprotected';
  } else if (warningCount > 0) {
    overallStatus = 'warning';
  }

  // Get most recent backup timestamp
  const lastBackup = assets
    .map(a => a.lastBackup)
    .filter(Boolean)
    .sort()
    .reverse()[0];

  // SaaS Protection assets carry service counts rather than a timestamp.
  const coverage = describeSaasCoverage(assets);

  const formatLastBackup = (timestamp) => {
    if (!timestamp) return coverage || 'No recent backup';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Less than 1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  return (
    <ProviderCard>
      <ProviderHeader>
        <ProviderIcon $color={color}>
          <IconComponent />
        </ProviderIcon>
        <ProviderName>{provider}</ProviderName>
      </ProviderHeader>

      <MetricRow>
        <MetricLabel>Assets</MetricLabel>
        <MetricValue>{assets.length}</MetricValue>
      </MetricRow>

      <MetricRow>
        <MetricLabel>Protected</MetricLabel>
        <MetricValue>{protectedCount}</MetricValue>
      </MetricRow>

      {warningCount > 0 && (
        <MetricRow>
          <MetricLabel>Warnings</MetricLabel>
          <MetricValue style={{ color: 'var(--odin-warning)' }}>{warningCount}</MetricValue>
        </MetricRow>
      )}

      {unprotectedCount > 0 && (
        <MetricRow>
          <MetricLabel>Unprotected</MetricLabel>
          <MetricValue style={{ color: 'var(--odin-error)' }}>{unprotectedCount}</MetricValue>
        </MetricRow>
      )}

      <StatusIndicator>
        <StatusDot $status={overallStatus} />
        <StatusText $status={overallStatus}>
          {overallStatus === 'protected' && 'All Protected'}
          {overallStatus === 'warning' && 'Some Warnings'}
          {overallStatus === 'unprotected' && 'Unprotected Assets'}
        </StatusText>
      </StatusIndicator>

      <LastBackupText>{formatLastBackup(lastBackup)}</LastBackupText>
    </ProviderCard>
  );
};

SaasProviderCard.propTypes = {
  provider: PropTypes.string.isRequired,
  assets: PropTypes.array.isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

export default SaasProviderCard;
